import { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { getAllCVs } from "../services/cvService";
import { CVDocument } from "../components/cv/CVDocument";
import CVData from "../types/CVData";
import { buildCvDocxBlob } from "../utils/cvToDocx";

export function useExportCV() {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportCVsByIds = async (userIds: string[], includeName: boolean) => {
    setIsExporting(true);
    setError(null);

    try {
      const data = await getAllCVs();
      const allCVs = Array.isArray(data) ? data : data.items;

      for (const id of userIds) {
        const user = allCVs.find((cv: CVData) => cv.cvId === id);

        if (!user) continue;

        const blob = await pdf(
          <CVDocument formData={user} includeName={includeName} />
        ).toBlob();

        const filename = includeName
          ? `${user.name}_CV.pdf`
          : `${user.cvUId}_CV.pdf`;

        saveAs(blob, filename);
      }
    } catch (err: any) {
      console.error("Export failed:", err);
      setError("Failed to export one or more CVs.");
    } finally {
      setIsExporting(false);
    }
  };

  const exportCVsByIdsAsDocx = async (
    userIds: string[],
    includeName: boolean
  ) => {
    setIsExporting(true);
    setError(null);

    try {
      const data = await getAllCVs();
      const allCVs = Array.isArray(data) ? data : data.items;

      for (const id of userIds) {
        const user = allCVs.find((cv: CVData) => cv.cvId === id);

        if (!user) continue;

        const blob = await buildCvDocxBlob(user, includeName);

        const filename = includeName
          ? `${user.name}_CV.docx`
          : `${user.cvUId}_CV.docx`;

        saveAs(blob, filename);
      }
    } catch (err: any) {
      console.error("DOCX export failed:", err);
      setError("Failed to export one or more CVs as Word.");
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCVsByIds, exportCVsByIdsAsDocx, isExporting, error };
}
